import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { Compass, Flag, Gavel, LayoutDashboard, LifeBuoy, Menu, MessagesSquare, ScrollText, Server, Shield, Users } from 'lucide-react';
import type { StaffAuditEntry } from '@spawnwire/shared';
import { Badge, Button, clsx, IconButton, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { useIsMobile } from '../../lib/hooks';
import { AUDIT_LABEL, formatDate } from '../../lib/moderation';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { DiscoveryPage } from './AdminDiscovery';
import { AppealsPage, ReportsPage } from './AdminQueues';
import { ServersPage, SpacesPage } from './AdminHosting';
import { SupportPage, TicketPage } from './AdminSupport';
import { UserDetailPage, UsersPage } from './AdminUsers';
import { Empty, PageHeader, StaffBadge, UserChip } from './adminUi';
import { loadAdminStats, useAdminStats } from './store';

const SECTIONS = [
  { path: '', label: 'Dashboard', icon: LayoutDashboard },
  { path: 'reports', label: 'Reports', icon: Flag },
  { path: 'appeals', label: 'Appeals', icon: Gavel },
  { path: 'support', label: 'Support', icon: LifeBuoy },
  { path: 'users', label: 'Users', icon: Users },
  { path: 'servers', label: 'Game servers', icon: Server },
  { path: 'spaces', label: 'Spaces', icon: MessagesSquare },
  { path: 'discovery', label: 'Discover review', icon: Compass },
  { path: 'audit', label: 'Audit log', icon: ScrollText },
] as const;

/** Takes the place of the conversation list while you are in the admin panel. */
export function AdminSidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useStore((s) => s.me);
  const stats = useAdminStats((s) => s.stats);
  const current = pathname.split('/')[3] ?? '';

  useEffect(() => {
    void loadAdminStats();
    const t = setInterval(() => void loadAdminStats(), 60_000);
    return () => clearInterval(t);
  }, []);

  const badge = (path: string) =>
    path === 'reports'
      ? (stats?.openReports ?? 0)
      : path === 'appeals'
        ? (stats?.openAppeals ?? 0)
        : path === 'support'
          ? (stats?.openTickets ?? 0)
          : path === 'discovery'
            ? (stats?.pendingDiscovery ?? 0)
            : 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-4 shadow-sm">
        <Shield className="size-5 text-danger" aria-hidden />
        <span className="font-semibold text-fg">Admin panel</span>
        <StaffBadge role={me?.staffRole ?? null} />
      </div>
      <nav aria-label="Admin sections" className="scroll-thin min-h-0 flex-1 overflow-y-auto p-2">
        {SECTIONS.map(({ path, label, icon: Icon }) => (
          <button
            key={path}
            type="button"
            onClick={() => {
              navigate(`/app/admin${path ? `/${path}` : ''}`);
              ui.setMobilePane('main');
            }}
            aria-current={current === path ? 'page' : undefined}
            className={clsx('mb-0.5 flex h-10 w-full items-center gap-3 rounded-md px-3 text-[15px] font-medium', current === path ? 'bg-active text-fg' : 'text-muted hover:bg-hover hover:text-fg')}
          >
            <Icon className="size-5" aria-hidden />
            <span className="flex-1 text-left">{label}</span>
            <Badge count={badge(path)} />
          </button>
        ))}
      </nav>
    </div>
  );
}

function Stat({ label, value, sub, onClick, tone }: { label: string; value: ReactNode; sub?: string; onClick?: () => void; tone?: string }) {
  const body = (
    <>
      <p className="text-xs font-semibold uppercase tracking-wide text-faint">{label}</p>
      <p className={clsx('mt-1 text-3xl font-bold', tone ?? 'text-fg')}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted">{sub}</p>}
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className="rounded-xl border border-line bg-sidebar p-4 text-left transition-colors hover:bg-elevated">
      {body}
    </button>
  ) : (
    <div className="rounded-xl border border-line bg-sidebar p-4">{body}</div>
  );
}

function AuditList({ entries }: { entries: StaffAuditEntry[] }) {
  const navigate = useNavigate();
  return (
    <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-sidebar">
      {entries.map((e) => {
        const reason = (e.details.reason ?? e.details.response ?? e.details.note) as string | undefined;
        const role = e.action === 'staff_role' ? ` to ${(e.details.to as string | null) ?? 'none'}` : '';
        const decision =
          e.action === 'appeal_review'
            ? ` (${e.details.decision === 'accept' ? 'accepted' : 'denied'})`
            : e.action === 'discovery_review'
              ? ` (${e.details.decision === 'approve' ? 'approved' : 'denied'})`
              : e.action === 'ticket_status'
                ? ` (${e.details.to === 'closed' ? 'closed' : 'reopened'})`
                : e.action === 'ticket_reply' && e.details.closed
                  ? ' (and closed it)'
                  : '';
        return (
          <li key={e.id} className="flex flex-wrap items-start gap-3 px-4 py-3 text-sm">
            <UserChip user={e.actor} size={28} />
            <div className="min-w-0 flex-1">
              <p className="text-fg">
                <span className="text-muted">{AUDIT_LABEL[e.action]}</span>{' '}
                {e.targetType === 'user' ? (
                  <button type="button" className="font-medium hover:underline" onClick={() => navigate(`/app/admin/users/${e.targetId}`)}>
                    @{e.targetLabel}
                  </button>
                ) : e.targetType === 'ticket' ? (
                  <button type="button" className="font-medium hover:underline" onClick={() => navigate(`/app/admin/support/${e.targetId}`)}>
                    {e.targetLabel}
                  </button>
                ) : e.targetType === 'appeal' ? (
                  <span className="font-medium">@{e.targetLabel}</span>
                ) : e.targetType === 'report' ? null : (
                  <span className="font-medium">{e.targetLabel}</span>
                )}
                {role}
                {decision}
              </p>
              {reason && <p className="mt-0.5 truncate text-xs text-muted">“{reason}”</p>}
            </div>
            <span className="text-xs text-faint">{formatDate(e.createdAt)}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const stats = useAdminStats((s) => s.stats);
  const [recent, setRecent] = useState<StaffAuditEntry[] | null>(null);

  useEffect(() => {
    void loadAdminStats();
    http
      .get<StaffAuditEntry[]>('/admin/audit')
      .then((rows) => setRecent(rows.slice(0, 8)))
      .catch(() => setRecent([]));
  }, []);

  return (
    <div>
      <PageHeader title="Dashboard" description="How Spawnwire is doing right now." />
      {!stats ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Users" value={stats.users.toLocaleString()} sub={`${stats.newUsers7d} new this week`} onClick={() => navigate('/app/admin/users')} />
          <Stat label="Online now" value={stats.online.toLocaleString()} tone="text-success" />
          <Stat label="Game servers" value={stats.servers} sub={`${stats.runningServers} running`} onClick={() => navigate('/app/admin/servers')} />
          <Stat label="Active bans" value={stats.activeBans} onClick={() => navigate('/app/admin/users')} />
          <Stat label="Open reports" value={stats.openReports} tone={stats.openReports ? 'text-warning' : undefined} onClick={() => navigate('/app/admin/reports')} />
          <Stat label="Open appeals" value={stats.openAppeals} tone={stats.openAppeals ? 'text-warning' : undefined} onClick={() => navigate('/app/admin/appeals')} />
          <Stat
            label="Support tickets"
            value={stats.openTickets}
            sub="Waiting for a reply"
            tone={stats.openTickets ? 'text-warning' : undefined}
            onClick={() => navigate('/app/admin/support')}
          />
          <Stat
            label="Discover requests"
            value={stats.pendingDiscovery}
            sub="Waiting for review"
            tone={stats.pendingDiscovery ? 'text-warning' : undefined}
            onClick={() => navigate('/app/admin/discovery')}
          />
        </div>
      )}
      <h2 className="mb-2 mt-8 text-sm font-semibold uppercase tracking-wide text-muted">Recent staff activity</h2>
      {!recent ? <Spinner /> : recent.length === 0 ? <Empty>Nothing yet.</Empty> : <AuditList entries={recent} />}
    </div>
  );
}

function AuditPage() {
  const [rows, setRows] = useState<StaffAuditEntry[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (offset = 0) => {
    try {
      const page = await http.get<StaffAuditEntry[]>(`/admin/audit?offset=${offset}`);
      setRows((prev) => (offset && prev ? [...prev, ...page] : page));
      setMore(page.length === 50);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <PageHeader title="Audit log" description="Everything Spawnwire staff have done, newest first. Entries can't be edited or deleted." />
      {error && <p className="mb-3 text-sm text-danger">{error}</p>}
      {!rows ? <Spinner /> : rows.length === 0 ? <Empty>Nothing yet.</Empty> : <AuditList entries={rows} />}
      {more && rows && (
        <Button variant="secondary" className="mt-3 w-full" onClick={() => void load(rows.length)}>
          Load more
        </Button>
      )}
    </div>
  );
}

/** The admin panel: only for Spawnwire staff (the server checks every request too). */
export function AdminView() {
  const isMobile = useIsMobile();
  const role = useStore((s) => s.me?.staffRole);
  if (!role) return <Navigate to="/app/@me" replace />;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {isMobile && (
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-3">
          <IconButton icon={Menu} label="Open admin sections" onClick={() => ui.setMobilePane('nav')} />
          <span className="flex items-center gap-2 font-semibold text-fg">
            <Shield className="size-5 text-danger" aria-hidden /> Admin panel
          </span>
        </header>
      )}
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto w-full max-w-5xl">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="appeals" element={<AppealsPage />} />
            <Route path="support" element={<SupportPage />} />
            <Route path="support/:ticketId" element={<TicketPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:userId" element={<UserDetailPage />} />
            <Route path="servers" element={<ServersPage />} />
            <Route path="spaces" element={<SpacesPage />} />
            <Route path="discovery" element={<DiscoveryPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="*" element={<Navigate to="/app/admin" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

